/**
 * Shared drafting-point constraint (docs/plans/2d/2d-full-parity-plan.md
 * Faz 0.4 + Faz 2.3): every CAD drafting tool resolves a raw ground point
 * through ONE pipeline — object snap (F3, resolved against the RAW cursor
 * point and winning over everything else), grid snap stepping (F9, store
 * cell size), polar tracking angle step (F10), then ortho (F8, relative to
 * the tool's base point).
 *
 * The pipeline also PUBLISHES the drafting feedback — the active osnap
 * marker and the statusbar `draftCursor` readout — from the same query the
 * click commits through, so the marker the user sees, the coordinate the
 * statusbar prints and the point the geometry lands on are always one and
 * the same (2D behavior).
 */

import * as THREE from 'three';
import { snapToGroundGrid, applyGroundOrtho } from '../../core/scene';
import { useSnapStore, type SnapPoint, type SnapType, type TrackingGuide } from './SnapManager';
import { resolveOsnap } from './snapSystemSync';
import { useAppStore } from '../../state/store';
import { useViewStore } from '../../state/commandAuxStores';
import { useSystemVarsStore } from '../../state/useSystemVarsStore';
import { isoAxes, isoConstrainWithAxes } from '../../core/drafting/isoPlane';

/** Snap `to` onto the nearest polar-tracking ray through `base` when the
 *  angle deviation from a POLARANG multiple is within the tracking tolerance. */
function applyPolarConstraint(base: THREE.Vector3, to: THREE.Vector3): THREE.Vector3 {
    const stepDeg = useSystemVarsStore.getState().POLARANG || 90;
    const step = (stepDeg * Math.PI) / 180;
    const dx = to.x - base.x, dy = to.y - base.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return to;
    const ang = Math.atan2(dy, dx);
    const nearest = Math.round(ang / step) * step;
    // Track when within ~3 degrees of the guide ray.
    if (Math.abs(ang - nearest) > (3 * Math.PI) / 180) return to;
    return new THREE.Vector3(base.x + Math.cos(nearest) * len, base.y + Math.sin(nearest) * len, to.z);
}

// ============================================================================
// OTRACK — object snap tracking (Faz 4.3, F11)
// ============================================================================

/** Recently acquired osnap targets: the alignment references OTRACK tracks.
 *  A small ring (module scope: no per-move allocation churn) over the points
 *  the snap engine actually published, so guides ride real acquired geometry
 *  instead of re-scanning the drawing on every pointer move. Each reference
 *  keeps its osnap kind so its guide renders in that snap's color (Faz 11.4). */
const OTRACK_REFERENCES: Array<{ point: THREE.Vector3; type: SnapType }> = [];
const OTRACK_REFERENCE_CAP = 8;

/** Alignment snap tolerance in world units (the classic small tracking pickbox). */
const OTRACK_TOLERANCE = 0.35;

/** Record an acquired osnap point as a future alignment reference. */
function rememberOtrackReference(snap: SnapPoint | null): void {
    if (!snap) return;
    const p = snap.position;
    const last = OTRACK_REFERENCES[OTRACK_REFERENCES.length - 1];
    if (last && last.point.distanceToSquared(p) < 1e-8) return;
    OTRACK_REFERENCES.push({ point: p.clone(), type: snap.type });
    if (OTRACK_REFERENCES.length > OTRACK_REFERENCE_CAP) OTRACK_REFERENCES.shift();
}

/** Draw the dashed guide for one engaged alignment: from the reference point
 *  through the constrained cursor, padded past it so the line reads as
 *  infinite (the AutoCAD dashed tracking vector). The guide keeps the
 *  reference's osnap kind for the type-colored rendering (Faz 11.4). */
function trackingGuide(ref: { point: THREE.Vector3; type: SnapType }, at: THREE.Vector3, axis: 'x' | 'y' | 'diag45' | 'diag135'): TrackingGuide {
    const dir = new THREE.Vector3();
    if (axis === 'x') dir.set(1, 0, 0);
    else if (axis === 'y') dir.set(0, 1, 0);
    else if (axis === 'diag45') dir.set(Math.SQRT1_2, Math.SQRT1_2, 0);
    else dir.set(Math.SQRT1_2, -Math.SQRT1_2, 0);
    const along = at.clone().sub(ref.point).dot(dir);
    const sign = along >= 0 ? 1 : -1;
    const pad = 2;
    const a = ref.point.clone().addScaledVector(dir, -sign * pad);
    const b = at.clone().addScaledVector(dir, sign * pad);
    return { from: [a.x, a.y, a.z], to: [b.x, b.y, b.z], type: ref.type };
}

/**
 * OTRACK alignment: snap `to` onto the horizontal / vertical / 45° line
 * through the closest tracked reference. Returns the snapped point and the
 * guides to draw, or null when nothing tracks. Polar-style angle snap on the
 * diagonal families uses the same tolerance as the axis families.
 */
function applyOtrackConstraint(to: THREE.Vector3): { point: THREE.Vector3; guides: TrackingGuide[] } | null {
    let best: { ref: { point: THREE.Vector3; type: SnapType }; axis: 'x' | 'y' | 'diag45' | 'diag135'; dev: number } | null = null;
    for (const ref of OTRACK_REFERENCES) {
        const dx = Math.abs(ref.point.x - to.x);
        const dy = Math.abs(ref.point.y - to.y);
        // 45° diagonals through the reference: |(x - refx) ∓ (y - refy)| / √2.
        const d45 = Math.abs((to.x - ref.point.x) - (to.y - ref.point.y)) / Math.SQRT2;
        const d135 = Math.abs((to.x - ref.point.x) + (to.y - ref.point.y)) / Math.SQRT2;
        if (dx < OTRACK_TOLERANCE && (!best || dx < best.dev)) best = { ref, axis: 'x', dev: dx };
        if (dy < OTRACK_TOLERANCE && (!best || dy < best.dev)) best = { ref, axis: 'y', dev: dy };
        if (d45 < OTRACK_TOLERANCE && (!best || d45 < best.dev)) best = { ref, axis: 'diag45', dev: d45 };
        if (d135 < OTRACK_TOLERANCE && (!best || d135 < best.dev)) best = { ref, axis: 'diag135', dev: d135 };
    }
    if (!best) return null;
    const p = to.clone();
    if (best.axis === 'x') p.x = best.ref.point.x;
    else if (best.axis === 'y') p.y = best.ref.point.y;
    else if (best.axis === 'diag45') {
        // Project onto x - refx = y - refy.
        const mid = ((to.x - best.ref.point.x) + (to.y - best.ref.point.y)) / 2;
        p.x = best.ref.point.x + mid;
        p.y = best.ref.point.y + mid;
    } else {
        // Project onto x - refx = -(y - refy).
        const mid = ((to.x - best.ref.point.x) - (to.y - best.ref.point.y)) / 2;
        p.x = best.ref.point.x + mid;
        p.y = best.ref.point.y - mid;
    }
    return { point: p, guides: [trackingGuide(best.ref, p, best.axis)] };
}

/** Publish the drafting feedback for a resolved point: the osnap marker
 *  (null when no target was acquired) and the statusbar readout point.
 *  Both store setters drop content-equal writes, so pointermove storms
 *  cost at most one re-render when the feedback actually changes. */
function publishDraftFeedback(snap: SnapPoint | null, point: THREE.Vector3): void {
    const store = useSnapStore.getState();
    store.setActiveSnap(snap);
    store.setDraftCursor(point);
    if (snap) rememberOtrackReference(snap);
}

/**
 * Resolve a raw world point into the final drafting point.
 * @param raw      ground-plane intersection of the cursor ray
 * @param camera   active viewport camera (osnap aperture + ortho unaffected)
 * @param gl       renderer for pixel->world aperture conversion
 * @param base     ortho/polar reference point (last committed point), if any
 */
export function constrainDraftPoint(
    raw: THREE.Vector3,
    camera: THREE.Camera,
    gl: THREE.WebGLRenderer,
    base?: THREE.Vector3 | null
): THREE.Vector3 {
    // Running object snaps (F3) resolve against the RAW cursor point — the
    // aperture is measured around the cursor, not around a pre-stepped
    // point — and the acquired target wins over every other constraint
    // (2D behavior: grid/polar/ortho stand down for a real snap target).
    // `base` unlocks the direction-dependent perpendicular mode.
    const snap = resolveOsnap(raw, camera, gl, base);
    if (snap) {
        // Iso axis lock (#312, Faz 7.1): while isodraft is on, only the
        // along-axis component of the osnap hit participates — the cursor
        // slides on the plane's axis through the acquired reference.
        if (base) {
            const axes = isoAxes();
            if (axes) {
                const locked = isoConstrainWithAxes(base, snap.position, axes);
                publishDraftFeedback(snap, locked);
                return locked;
            }
        }
        publishDraftFeedback(snap, snap.position);
        return snap.position;
    }

    const snapState = useSnapStore.getState();
    let p = raw.clone();
    // Grid snap stepping (F9): independent from object snap.
    if (snapState.gridSnapEnabled) {
        p = snapToGroundGrid(p, snapState.gridSize);
    }
    // OTRACK (F11): alignment snap to X/Y/45° lines through recently
    // acquired osnap points. Runs after osnap/grid, before polar/ortho, so
    // the acquired-tracking target wins over the angle-step constraints and
    // the resulting point still feeds ortho below (constraint composition).
    if (snapState.objectSnapTracking) {
        const track = applyOtrackConstraint(p);
        if (track) {
            p = track.point;
            snapState.setTrackingGuides(track.guides);
        } else {
            snapState.setTrackingGuides([]);
        }
    } else if (snapState.trackingGuides.length > 0) {
        snapState.setTrackingGuides([]);
    }
    // Polar tracking (F10): angle-step constraint.
    if (base && useViewStore.getState().polarOn) {
        p = applyPolarConstraint(base, p);
    }
    // Isodraft (Faz 7.1): the cursor projects onto the dominant axis of the
    // active iso plane (OCS drafting_constrain) — replaces ortho while on.
    if (base && useViewStore.getState().isoDrafting) {
        p = isoConstrainWithAxes(base, p, isoAxes()!);
    } else if (base && (useAppStore.getState().orthoMode || useAppStore.getState().shiftOrthoActive)) {
        // Ortho (F8 / Shift hold) relative to the last committed point. The
        // store's shiftOrthoActive flag implements the temporary Shift override.
        p = applyGroundOrtho(base, p, true);
    }
    // No target under the cursor: marker rests, readout follows the
    // constrained cursor.
    publishDraftFeedback(null, p);
    return p;
}
